import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import DealCard from './DealCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DealsList = ({ mode = 'all' }) => {
  // mode: 'all' | 'category' | 'store'
  const { slug } = useParams();
  const [meta, setMeta] = useState({ categories: [], stores: [] });
  const [params, setParams] = useSearchParams();
  const [deals, setDeals] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [pageInfo, setPageInfo] = useState(null);

  const sort = params.get('sort') || 'score';
  const minDiscount = params.get('min_discount') || '';
  const maxPrice = params.get('max_price') || '';
  const quality = params.get('quality') || '';
  const store = params.get('store') || (mode === 'store' ? slug : '');
  const category = params.get('category') || (mode === 'category' ? slug : '');

  const title = useMemo(() => {
    if (mode === 'category') {
      const c = meta.categories.find(x => x.slug === slug);
      return c ? `Best ${c.name.toLowerCase()} deals today` : 'Deals';
    }
    if (mode === 'store') {
      const s = meta.stores.find(x => x.slug === slug);
      return s ? `Best ${s.name} deals today` : 'Deals';
    }
    return 'All hot deals';
  }, [mode, slug, meta]);

  useEffect(() => {
    axios.get(`${API}/deals/meta`).then(r => setMeta(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        let url;
        const q = new URLSearchParams();
        q.set('limit', '24');
        if (sort) q.set('sort', sort);
        if (minDiscount) q.set('min_discount', minDiscount);
        if (maxPrice) q.set('max_price', maxPrice);
        if (quality) q.set('quality', quality);

        if (mode === 'category') {
          url = `${API}/deals/category/${slug}?${q.toString()}`;
        } else if (mode === 'store') {
          url = `${API}/deals/store/${slug}?${q.toString()}`;
        } else {
          if (store) q.set('store', store);
          if (category) q.set('category', category);
          url = `${API}/deals?${q.toString()}`;
        }
        const r = await axios.get(url);
        setDeals(r.data.deals || []);
        setTotal(r.data.total || 0);
        setPageInfo(r.data.category || r.data.store || null);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [mode, slug, sort, minDiscount, maxPrice, quality, store, category]);

  const setFilter = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="mb-8">
        <h1 data-testid="deals-list-title" className="text-3xl sm:text-4xl font-extrabold">{title}</h1>
        <p className="text-zinc-400 mt-1 text-sm">{total} deal{total === 1 ? '' : 's'} found</p>
      </div>

      <div className="flex flex-wrap gap-2 mb-6 items-center">
        <select
          data-testid="filter-sort"
          value={sort}
          onChange={e => setFilter('sort', e.target.value)}
          className="bg-zinc-900 border border-zinc-800 text-white text-sm px-3 py-2 rounded-md"
        >
          <option value="score">Top scored</option>
          <option value="newest">Newest</option>
          <option value="discount">Biggest discount</option>
          <option value="price_asc">Price: low to high</option>
          <option value="price_desc">Price: high to low</option>
        </select>
        <select
          data-testid="filter-quality"
          value={quality}
          onChange={e => setFilter('quality', e.target.value)}
          className="bg-zinc-900 border border-zinc-800 text-white text-sm px-3 py-2 rounded-md"
        >
          <option value="">Any quality</option>
          <option value="hot">Hot deals only</option>
          <option value="good">Good & above</option>
        </select>
        <select
          data-testid="filter-min-discount"
          value={minDiscount}
          onChange={e => setFilter('min_discount', e.target.value)}
          className="bg-zinc-900 border border-zinc-800 text-white text-sm px-3 py-2 rounded-md"
        >
          <option value="">Any discount</option>
          <option value="20">20%+ off</option>
          <option value="30">30%+ off</option>
          <option value="50">50%+ off</option>
        </select>
        <input
          data-testid="filter-max-price"
          type="number"
          placeholder="Max price $"
          value={maxPrice}
          onChange={e => setFilter('max_price', e.target.value)}
          className="bg-zinc-900 border border-zinc-800 text-white text-sm px-3 py-2 rounded-md w-36"
        />
        {mode === 'all' && (
          <>
            <select
              data-testid="filter-category"
              value={category}
              onChange={e => setFilter('category', e.target.value)}
              className="bg-zinc-900 border border-zinc-800 text-white text-sm px-3 py-2 rounded-md"
            >
              <option value="">All categories</option>
              {meta.categories.map(c => <option key={c.slug} value={c.slug}>{c.name}</option>)}
            </select>
            <select
              data-testid="filter-store"
              value={store}
              onChange={e => setFilter('store', e.target.value)}
              className="bg-zinc-900 border border-zinc-800 text-white text-sm px-3 py-2 rounded-md"
            >
              <option value="">All stores</option>
              {meta.stores.map(s => <option key={s.slug} value={s.slug}>{s.name}</option>)}
            </select>
          </>
        )}
      </div>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="bg-zinc-900 border border-zinc-800 rounded-xl animate-pulse h-80" />
          ))}
        </div>
      ) : deals.length === 0 ? (
        <div data-testid="no-results" className="text-zinc-400 py-16 text-center">
          No deals match these filters yet.
        </div>
      ) : (
        <div data-testid="deals-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {deals.map(d => <DealCard key={d.id} deal={d} />)}
        </div>
      )}
    </div>
  );
};

export default DealsList;
